import {
	Box,
	Chip,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	OutlinedInput,
	Select,
	type SelectChangeEvent,
} from "@mui/material";
import { useState } from "react";
import { useStands } from "@/shared/hooks/useStands";

interface StandsMultiSelectProps {
	/** First id is the main stand of the pass; the rest are the extra stands. */
	value: number[];
	/** Called once, when the list closes, so picking several stands is one save. */
	onChange: (value: number[]) => void;
	required?: boolean;
	error?: boolean;
	helperText?: string;
}

export const StandsMultiSelect = ({
	value,
	onChange,
	required = false,
	error = false,
	helperText,
}: StandsMultiSelectProps) => {
	const { data: stands = [], isLoading } = useStands({
		includeStraighteners: true,
	});
	const [draft, setDraft] = useState<number[] | null>(null);
	const nameById = new Map(stands.map((s) => [s.Id, s.Name]));
	const numberById = new Map(stands.map((s) => [s.Id, s.StandNumber ?? 0]));
	const shown = draft ?? value;

	// Keep the main stand first and the rest in mill order.
	const handleChange = (event: SelectChangeEvent<number[]>) => {
		const selected = event.target.value as number[];
		const main =
			shown.length > 0 && selected.includes(shown[0]) ? shown[0] : selected[0];
		const extras = selected
			.filter((id) => id !== main)
			.sort((a, b) => (numberById.get(a) ?? 0) - (numberById.get(b) ?? 0));
		setDraft(main == null ? [] : [main, ...extras]);
	};

	const handleClose = () => {
		if (draft && draft.join(",") !== value.join(",")) onChange(draft);
		setDraft(null);
	};

	return (
		<FormControl
			fullWidth
			size="small"
			margin="dense"
			required={required}
			error={error}
		>
			<InputLabel id="pass-stands-label" shrink>
				Stands
			</InputLabel>
			<Select<number[]>
				labelId="pass-stands-label"
				multiple
				value={shown}
				onChange={handleChange}
				onOpen={() => setDraft(value)}
				onClose={handleClose}
				disabled={isLoading}
				input={<OutlinedInput label="Stands" notched />}
				sx={{ "& .MuiSelect-select": { py: "7px" } }}
				renderValue={(selected) => (
					<Box
						sx={{
							display: "flex",
							flexWrap: "nowrap",
							gap: 0.5,
							overflow: "hidden",
						}}
						title={selected.map((id) => nameById.get(id) ?? id).join(", ")}
					>
						{selected.map((id) => (
							<Chip
								key={id}
								size="small"
								label={nameById.get(id) ?? id}
								sx={{ height: 22 }}
							/>
						))}
					</Box>
				)}
				MenuProps={{ slotProps: { paper: { sx: { maxHeight: 320 } } } }}
			>
				{stands.map((stand) => (
					<MenuItem key={stand.Id} value={stand.Id}>
						{stand.Name}
					</MenuItem>
				))}
			</Select>
			{helperText && <FormHelperText>{helperText}</FormHelperText>}
		</FormControl>
	);
};
